import { Link } from "react-router-dom";
import { Shield, Cloud, Server, Monitor, Lock, Camera, ArrowRight, CheckCircle } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  {
    icon: Monitor,
    title: "IT Services",
    desc: "Custom software development, application management, and end-to-end IT consulting.",
  },
  {
    icon: Server,
    title: "ERP Services",
    desc: "Enterprise resource planning implementation, customization, and support for seamless operations.",
  },
  {
    icon: Cloud,
    title: "Cloud Services",
    desc: "Cloud migration, managed hosting, and scalable infrastructure for modern businesses.",
  },
  {
    icon: Shield,
    title: "IT Infrastructure",
    desc: "Network design, data center solutions, and hardware provisioning for robust IT foundations.",
  },
  {
    icon: Lock,
    title: "Cyber Security",
    desc: "Threat assessment, vulnerability management, and compliance-ready security solutions.",
  },
  {
    icon: Camera,
    title: "Security & Surveillance",
    desc: "CCTV systems, access control, and integrated monitoring for physical security.",
  },
];

const stats = [
  { value: "200+", label: "Projects Delivered" },
  { value: "50+", label: "Enterprise Clients" },
  { value: "10+", label: "Years Experience" },
  { value: "99.9%", label: "Uptime SLA" },
];

export default function HomePage() {
  usePageMeta(null, "Vaana BuildLogic Technologies delivers enterprise-grade IT services, ERP solutions, cloud infrastructure, and cyber security in New Delhi.");
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Server infrastructure"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 pt-40">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-6 animate-fade-in-up">
              Enterprise IT Solutions & Cloud Infrastructure
            </p>
            <h1 className="text-5xl sm:text-6xl tracking-tight leading-none font-['Outfit'] font-bold text-white mb-6 animate-fade-in-up animation-delay-100">
              Building Digital Infrastructure for Tomorrow
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed mb-10 max-w-2xl animate-fade-in-up animation-delay-200">
              Vaana BuildLogic Technologies delivers enterprise-grade IT services, ERP solutions, and cloud infrastructure. We help businesses scale securely with cutting-edge technology.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#00E5FF] text-[#060B19] font-semibold text-sm rounded-sm hover:bg-[#33EEFF] transition-all duration-300 btn-glow"
              >
                Get Started <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#00E5FF] text-[#00E5FF] font-semibold text-sm rounded-sm hover:bg-[#00E5FF]/10 transition-all duration-300"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0A122A] border-y border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-center">
                <p className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#00E5FF] mb-2">
                  {stat.value}
                </p>
                <p className="text-sm text-[#94A3B8]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-overview" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-4">
              What We Offer
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-['Outfit'] font-bold text-white mb-4">
              Comprehensive IT Solutions
            </h2>
            <p className="text-[#94A3B8] max-w-2xl">
              From cloud infrastructure to cyber security, we provide end-to-end technology solutions tailored to your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className="service-card p-8 bg-[#0A122A] border border-white/8 rounded-md group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#111D3D] rounded-md mb-5 group-hover:bg-[#00E5FF]/10 transition-colors duration-300">
                  <service.icon size={24} className="text-[#00E5FF]" />
                </div>
                <h3 className="text-xl font-['Outfit'] font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                  {service.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="view-all-services"
              className="inline-flex items-center gap-2 text-[#00E5FF] text-sm font-medium hover:text-[#33EEFF] transition-colors duration-300"
            >
              View All Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#0A122A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-4">
                Why Choose Us
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-['Outfit'] font-bold text-white mb-6">
                Trusted Technology Partner for Growing Businesses
              </h2>
              <p className="text-[#94A3B8] mb-8 leading-relaxed">
                We combine deep technical expertise with a client-first approach to deliver solutions that drive real business outcomes. Our team of certified professionals ensures your technology infrastructure is secure, scalable, and future-ready.
              </p>
              <ul className="space-y-4">
                {[
                  "24/7 Technical Support & Monitoring",
                  "Certified Cloud & Security Experts",
                  "Scalable Solutions for Any Business Size",
                  "Proven Track Record with 200+ Projects",
                  "Compliance-Ready Infrastructure",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#00E5FF] flex-shrink-0" />
                    <span className="text-sm text-[#94A3B8]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7964413/pexels-photo-7964413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional team"
                className="rounded-md border border-white/8 w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#111D3D] border border-white/10 rounded-md p-6 hidden lg:block">
                <p className="text-2xl font-bold font-['Outfit'] text-[#00E5FF]">10+</p>
                <p className="text-xs text-[#94A3B8]">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-['Outfit'] font-bold text-white mb-4">
            Ready to Transform Your IT Infrastructure?
          </h2>
          <p className="text-[#94A3B8] max-w-xl mx-auto mb-10">
            Let our experts help you build a secure, scalable technology foundation for your business.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-button"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5FF] text-[#060B19] font-semibold text-sm rounded-sm hover:bg-[#33EEFF] transition-all duration-300 btn-glow"
          >
            Contact Us Today <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
